import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Add,
    Eye,
    Link1,
    ScanBarcode,
    Send2,
    TickCircle,
    TickSquare,
} from "iconsax-react";
import DBModal from "../../component/global/DBModal";
import DBButton from "../../component/global/DBButton";
import DBSpinner from "../../component/global/DBSpinner";
import DBConfirmModal from "../../component/global/DBConfirmModal";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SURVEY_CAMPAIGNS,
    surveyActionUrl,
    surveyCampaignUrl,
    surveyDetailUrl,
    surveyPublicPageUrl,
} from "../../helperData/ApiAddress";
import {
    CAMPAIGN_KIND,
    CAMPAIGN_STATUS,
    CLUB_USER_AUDIENCES,
    MESSAGE_TYPE,
    SURVEY_STATUS,
    USER_AUDIENCE,
    campaignBelongsToSurvey,
    campaignIsSendable,
    campaignIsUnbound,
    campaignKindLabel,
    campaignDeliveryLabel,
    campaignStatusLabel,
    campaignsForSurveyWizard,
    campaignFitsSurvey,
    findUsersCampaignForAudience,
    formatSurveyDateTime,
    isLinkCampaign,
    isPhoneKind,
    pickWizardCampaignId,
    resolveInSystemAudiences,
    wizardDistributionCampaigns,
} from "./constants";
import CampaignModal from "./CampaignModal";
import SurveyFormModal from "./SurveyFormModal";
import FeatureFieldsEditor from "./FeatureFieldsEditor";
import {
    featuresFromApi,
    featuresToPayload,
    validateFeatureRows,
} from "./featureFields";
import CampaignWizard from "../../features/incentives/CampaignWizard";
import { translateServerDetail } from "./translateServerDetail";
import { campaignSendQuoteSpec, SURVEY_SEND_OPERATION } from "./sendBilling";
import { isInsufficientCoins, useAiCoinGate } from "../../features/ai-billing/AiCoinGate";

const IN_SYSTEM_AUDIENCE_LABEL = {
    [USER_AUDIENCE.ALL]: "survey.wizard.send.audience.all",
    [USER_AUDIENCE.PUBLIC]: "survey.wizard.send.audience.public",
    [USER_AUDIENCE.ORG]: "survey.wizard.send.audience.org",
    [USER_AUDIENCE.SPECIAL]: "survey.wizard.send.audience.special",
};

const WIZARD_PHASE = {
    DEFINE: "define",
    FIELDS: "fields",
    CAMPAIGN: "campaign",
    INCENTIVE: "incentive",
    SEND: "send",
};

const WIZARD_STEPS = [
    WIZARD_PHASE.DEFINE,
    WIZARD_PHASE.FIELDS,
    WIZARD_PHASE.CAMPAIGN,
    WIZARD_PHASE.INCENTIVE,
    WIZARD_PHASE.SEND,
];

const stepClassName = (done, current) => {
    if (done) return "is-done";
    if (current) return "is-current";
    return "";
};

export default function CampaignSetupModal({
    show,
    survey,
    onHide,
    onSurveyChange,
    startAtFields = false,
    linkOnly = false,
}) {
    const toast = useContext(ToastContext);
    const [campaigns, setCampaigns] = useState([]);
    const [surveyState, setSurveyState] = useState(survey);
    const [phase, setPhase] = useState(WIZARD_PHASE.CAMPAIGN);
    const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
    const [inSystemAudiences, setInSystemAudiences] = useState([]);
    const [incentiveWizard, setIncentiveWizard] = useState(false);
    const [defineModal, setDefineModal] = useState(false);
    const [featureRows, setFeatureRows] = useState([]);
    const [featureErrors, setFeatureErrors] = useState([]);
    const [fieldsSaved, setFieldsSaved] = useState(false);
    const pendingAfterCreateRef = useRef(false);
    const incentiveOfferedRef = useRef(false);
    const clubParentRef = useRef(null);
    const [campaignModal, setCampaignModal] = useState({
        show: false,
        campaign: null,
        isCreate: false,
    });
    const [confirmSend, setConfirmSend] = useState(null);
    const [sentCampaignIds, setSentCampaignIds] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    const activeSurvey = surveyState || survey;
    const [getCampaigns, getCampaignsLoading] = useRequest(
        SURVEY_CAMPAIGNS,
        "GET"
    );
    const [getSurvey] = useRequest(
        activeSurvey?.id ? surveyDetailUrl(activeSurvey.id) : "",
        "GET"
    );
    const [saveFeatures, saveFeaturesLoading] = useRequest(
        activeSurvey?.id ? surveyDetailUrl(activeSurvey.id) : "",
        "PATCH"
    );

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
        "Accept-Language": i18n.language || "fa",
    });

    const hasCampaign = campaigns.length > 0;
    const distributionCampaigns = useMemo(
        () => wizardDistributionCampaigns(campaigns),
        [campaigns]
    );
    const selectedCampaigns = distributionCampaigns.filter(
        (row) =>
            selectedCampaignIds.includes(row.id) &&
            campaignFitsSurvey(row, activeSurvey)
    );
    const selectedSendableCampaigns = selectedCampaigns.filter(campaignIsSendable);
    const resolvedInSystemAudiences = linkOnly
        ? []
        : resolveInSystemAudiences(inSystemAudiences);
    const hasInSystemSelection = resolvedInSystemAudiences.length > 0;
    const hasSendConfig = hasCampaign || inSystemAudiences.length > 0;
    const canSend =
        selectedSendableCampaigns.length > 0 || hasInSystemSelection;
    const allUsersSelected = inSystemAudiences.includes(USER_AUDIENCE.ALL);
    const clubSelectedCount = CLUB_USER_AUDIENCES.filter((audience) =>
        inSystemAudiences.includes(audience)
    ).length;
    const clubAllSelected = clubSelectedCount === CLUB_USER_AUDIENCES.length;
    const clubSomeSelected = clubSelectedCount > 0 && !clubAllSelected;
    const hasSentInSession = sentCampaignIds.length > 0;
    const wizardSteps = linkOnly
        ? [
              WIZARD_PHASE.DEFINE,
              WIZARD_PHASE.FIELDS,
              WIZARD_PHASE.CAMPAIGN,
              WIZARD_PHASE.SEND,
          ]
        : WIZARD_STEPS;
    const currentStep = wizardSteps.indexOf(phase) + 1;

    const incentivePreset = useMemo(
        () => ({
            action: "survey_submit",
            surveyId: activeSurvey?.id || null,
            surveyTitle: activeSurvey?.title || "",
            name: t("survey.wizard.incentive.name", {
                title: activeSurvey?.title || "",
            }),
            lockTarget: true,
        }),
        [activeSurvey?.id, activeSurvey?.title]
    );

    const loadCampaigns = (preferredId) => {
        getCampaigns({
            page_size: 100,
            ...(linkOnly ? { scope: "personal" } : {}),
        })
            .then((r) => {
                const rows = campaignsForSurveyWizard(r.results || [], {
                    surveyId: activeSurvey?.id,
                    linkOnly,
                });
                setCampaigns(rows);
                const distribution = wizardDistributionCampaigns(rows).filter(
                    (row) => campaignFitsSurvey(row, activeSurvey)
                );
                setSelectedCampaignIds((current) => {
                    const valid = current.filter((id) =>
                        distribution.some((row) => row.id === id)
                    );
                    if (valid.length) return valid;
                    const picked = pickWizardCampaignId(
                        distribution,
                        preferredId || current[0],
                        activeSurvey?.id
                    );
                    return picked ? [picked] : [];
                });
            })
            .catch(() => setCampaigns([]));
    };

    useEffect(() => {
        if (!show) {
            setCampaignModal({ show: false, campaign: null, isCreate: false });
            setConfirmSend(null);
            setSentCampaignIds([]);
            setPhase(startAtFields ? WIZARD_PHASE.FIELDS : WIZARD_PHASE.CAMPAIGN);
            setSelectedCampaignIds([]);
            setInSystemAudiences([]);
            setIncentiveWizard(false);
            setDefineModal(false);
            setFeatureRows([]);
            setFeatureErrors([]);
            setFieldsSaved(false);
            pendingAfterCreateRef.current = false;
            incentiveOfferedRef.current = false;
            return;
        }
        setSurveyState(survey);
        setCampaignModal({ show: false, campaign: null, isCreate: false });
        setConfirmSend(null);
        setSentCampaignIds([]);
        setPhase(startAtFields ? WIZARD_PHASE.FIELDS : WIZARD_PHASE.CAMPAIGN);
        setSelectedCampaignIds([]);
        setInSystemAudiences([]);
        setIncentiveWizard(false);
        setDefineModal(false);
        setFeatureRows([]);
        setFeatureErrors([]);
        setFieldsSaved(false);
        pendingAfterCreateRef.current = false;
        incentiveOfferedRef.current = false;
        loadCampaigns();
        if (survey?.id) {
            getSurvey().then((row) => {
                setFeatureRows(featuresFromApi(row.features));
                setSurveyState((current) => ({ ...(current || {}), ...row }));
            }).catch(() => setFeatureRows([]));
        }
    }, [show, survey?.id]);

    const openSurveyIfNeeded = () => {
        const surveyId = activeSurvey?.id;
        if (!surveyId) return Promise.resolve(activeSurvey);
        if (activeSurvey?.status === SURVEY_STATUS.OPEN) {
            return Promise.resolve(activeSurvey);
        }
        return axios
            .post(surveyActionUrl(surveyId, "open"), {}, { headers: authHeaders() })
            .then((r) => {
                const opened = r.data;
                if (opened?.id) {
                    setSurveyState(opened);
                    onSurveyChange?.(opened);
                    toast(t("survey.wizard.opened"), "success");
                }
                return opened;
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.status.change.error"), "danger");
                return activeSurvey;
            });
    };

    const continueAfterDistributionCampaign = () => {
        openSurveyIfNeeded();
        if (!linkOnly && !incentiveOfferedRef.current) {
            incentiveOfferedRef.current = true;
            setPhase(WIZARD_PHASE.INCENTIVE);
            return;
        }
        setPhase(WIZARD_PHASE.SEND);
    };

    const finishCampaignModal = () => {
        setCampaignModal({ show: false, campaign: null, isCreate: false });
        if (!pendingAfterCreateRef.current) return;
        pendingAfterCreateRef.current = false;
        continueAfterDistributionCampaign();
    };

    const handleCopyLink = (campaign) => {
        const url = surveyPublicPageUrl(campaign.public_url);
        navigator.clipboard
            ?.writeText(url)
            .then(() => toast(t("survey.campaign.link.copied"), "success"))
            .catch(() => toast(t("survey.campaign.link.copied"), "success"));
    };

    const handleQr = (campaign) => {
        axios
            .get(surveyCampaignUrl(campaign.id, "qr-code"), {
                headers: authHeaders(),
                responseType: "blob",
            })
            .then((r) => {
                const url = URL.createObjectURL(r.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = `survey-campaign-${campaign.id}.png`;
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch(() => toast(t("survey.campaign.send.error"), "danger"));
    };

    useEffect(() => {
        if (!clubParentRef.current) return;
        clubParentRef.current.indeterminate =
            clubSomeSelected && !allUsersSelected;
    }, [clubSomeSelected, allUsersSelected]);

    const toggleCampaignId = (id) => {
        setSelectedCampaignIds((current) =>
            current.includes(id)
                ? current.filter((item) => item !== id)
                : [...current, id]
        );
    };

    const toggleAudience = (audience) => {
        setInSystemAudiences((current) =>
            current.includes(audience)
                ? current.filter((item) => item !== audience)
                : [...current, audience]
        );
    };

    const toggleClubParent = () => {
        setInSystemAudiences((current) => {
            if (CLUB_USER_AUDIENCES.every((audience) => current.includes(audience))) {
                return current.filter(
                    (item) => !CLUB_USER_AUDIENCES.includes(item)
                );
            }
            const next = current.filter(
                (item) => !CLUB_USER_AUDIENCES.includes(item)
            );
            return [...next, ...CLUB_USER_AUDIENCES];
        });
    };

    const openCreateCampaign = () => {
        setCampaignModal({
            show: true,
            campaign: null,
            isCreate: true,
        });
    };

    const handleConfirmSend = (payloadOverride) => {
        const payload = payloadOverride || confirmSend;
        const campaignTargets = payload?.campaigns || [];
        const audiences = payload?.audiences || [];
        const surveyId = activeSurvey?.id;
        if (!surveyId || (!campaignTargets.length && !audiences.length)) return;
        setActionLoading(true);
        const headers = authHeaders();
        const sendOne = (campaign) =>
            axios.post(
                surveyCampaignUrl(campaign.id, "send"),
                { survey: surveyId },
                { headers }
            );
        const ensureAudienceCampaign = (audience) => {
            const existing = findUsersCampaignForAudience(
                campaigns,
                audience,
                surveyId
            );
            const created = existing
                ? Promise.resolve(existing)
                : axios
                      .post(
                          SURVEY_CAMPAIGNS,
                          {
                              survey: surveyId,
                              name: t("survey.wizard.in_system.campaign.name", {
                                  audience: t(
                                      IN_SYSTEM_AUDIENCE_LABEL[audience]
                                  ),
                              }),
                              kind: CAMPAIGN_KIND.USERS,
                              user_group_type:
                                  audience === USER_AUDIENCE.ALL
                                      ? ""
                                      : audience,
                              message_type: MESSAGE_TYPE.ADMINISTRATIVE,
                          },
                          { headers }
                      )
                      .then((r) => r.data);
            return created.then((campaign) => {
                if (
                    campaign.status !== CAMPAIGN_STATUS.DRAFT &&
                    campaign.status !== CAMPAIGN_STATUS.SCHEDULED
                ) {
                    return campaign;
                }
                return axios
                    .post(
                        surveyCampaignUrl(campaign.id, "populate-users"),
                        {},
                        { headers }
                    )
                    .then(() => campaign);
            });
        };
        const openIfNeeded =
            activeSurvey?.status === SURVEY_STATUS.OPEN
                ? Promise.resolve(activeSurvey)
                : axios
                      .post(
                          surveyActionUrl(surveyId, "open"),
                          {},
                          { headers }
                      )
                      .then((r) => r.data);

        openIfNeeded
            .then((opened) => {
                if (opened?.id) {
                    setSurveyState(opened);
                    onSurveyChange?.(opened);
                }
                return audiences.reduce(
                    (chain, audience) =>
                        chain.then((rows) =>
                            ensureAudienceCampaign(audience).then((row) => [
                                ...rows,
                                row,
                            ])
                        ),
                    Promise.resolve([])
                );
            })
            .then((audienceCampaigns) => {
                const queue = [...campaignTargets, ...audienceCampaigns];
                return queue.reduce(
                    (chain, campaign) =>
                        chain.then((ids) =>
                            sendOne(campaign).then((response) => [
                                ...ids,
                                response?.data?.id || campaign.id,
                            ])
                        ),
                    Promise.resolve([])
                );
            })
            .then((sentIds) => {
                toast(t("survey.campaign.send.started"), "success");
                setConfirmSend(null);
                setSentCampaignIds((ids) => [
                    ...ids,
                    ...sentIds.filter((id) => !ids.includes(id)),
                ]);
                loadCampaigns(sentIds[sentIds.length - 1]);
            })
            .catch((e) => {
                if (isInsufficientCoins(e)) {
                    openFromError(e, () => handleConfirmSend(payload));
                    return;
                }
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.campaign.send.error"), "danger");
                setConfirmSend(null);
            })
            .finally(() => setActionLoading(false));
    };

    const requestSend = () => {
        const payload = {
            campaigns: selectedSendableCampaigns,
            audiences: resolvedInSystemAudiences,
        };
        if (!payload.campaigns.length && !payload.audiences.length) return;
        const billable = payload.campaigns
            .map(campaignSendQuoteSpec)
            .find(Boolean);
        const spec =
            billable ||
            (payload.audiences.length
                ? {
                      operation: SURVEY_SEND_OPERATION.IN_APP,
                      quantity: payload.audiences.length,
                  }
                : null);
        if (spec) {
            confirmAndRun(spec, () => handleConfirmSend(payload));
            return;
        }
        setConfirmSend(payload);
    };

    const campaignWasSent = (campaign) =>
        sentCampaignIds.includes(campaign?.id) ||
        campaign?.status === CAMPAIGN_STATUS.SENT ||
        campaign?.status === CAMPAIGN_STATUS.SENDING;

    const openCampaignDetails = (campaign) => {
        if (!campaign) return;
        setSelectedCampaignIds((current) =>
            current.includes(campaign.id) ? current : [...current, campaign.id]
        );
        setCampaignModal({
            show: true,
            campaign,
            isCreate: false,
        });
    };

    const campaignMetaLine = (campaign) => {
        const parts = [];
        if (!isLinkCampaign(campaign.kind)) {
            parts.push(
                `${t("survey.campaign.table.recipients")}: ${
                    campaign.recipients_count ?? 0
                }`,
                `${t("survey.campaign.table.sent")}: ${
                    campaign.sent_count ?? 0
                }`
            );
            if (campaign.completed_count) {
                parts.push(
                    `${t("survey.campaign.table.completed")}: ${
                        campaign.completed_count
                    }`
                );
            }
        }
        if (campaign.scheduled_at) {
            parts.push(
                `${t("survey.campaign.scheduled")}: ${formatSurveyDateTime(
                    campaign.scheduled_at
                )}`
            );
        }
        if (campaign.message_subject) {
            parts.push(campaign.message_subject);
        }
        return parts.join(" · ");
    };

    const goToStep = (step) => {
        const next = wizardSteps[step - 1];
        if (!next) return;
        if (
            (next === WIZARD_PHASE.INCENTIVE || next === WIZARD_PHASE.SEND) &&
            !hasSendConfig
        ) {
            return;
        }
        setPhase(next);
    };

    const nextPhase = wizardSteps[wizardSteps.indexOf(phase) + 1] || null;
    const nextStepCaption = (() => {
        if (!nextPhase) return "";
        const labelKey = {
            [WIZARD_PHASE.FIELDS]: "survey.wizard.step.fields",
            [WIZARD_PHASE.CAMPAIGN]: "survey.wizard.step.campaign",
            [WIZARD_PHASE.INCENTIVE]: "survey.wizard.step.coins",
            [WIZARD_PHASE.SEND]: "survey.wizard.step.send",
        }[nextPhase];
        return t("survey.wizard.next", { step: t(labelKey) });
    })();

    const goBack = () => {
        if (phase === WIZARD_PHASE.SEND) {
            setPhase(
                linkOnly ? WIZARD_PHASE.CAMPAIGN : WIZARD_PHASE.INCENTIVE
            );
            return;
        }
        if (phase === WIZARD_PHASE.INCENTIVE) {
            setPhase(WIZARD_PHASE.CAMPAIGN);
            return;
        }
        if (phase === WIZARD_PHASE.CAMPAIGN) {
            setPhase(WIZARD_PHASE.FIELDS);
            return;
        }
        if (phase === WIZARD_PHASE.FIELDS) {
            setPhase(WIZARD_PHASE.DEFINE);
        }
    };

    const saveFieldsAndContinue = () => {
        const nextErrors = validateFeatureRows(featureRows);
        setFeatureErrors(nextErrors);
        if (nextErrors.some(Boolean)) return;
        if (!activeSurvey?.id) {
            setPhase(WIZARD_PHASE.CAMPAIGN);
            return;
        }
        saveFeatures({
            features: featuresToPayload(featureRows),
            version: activeSurvey.version,
        })
            .then((saved) => {
                if (saved?.id) {
                    setSurveyState(saved);
                    onSurveyChange?.(saved);
                    setFeatureRows(featuresFromApi(saved.features));
                }
                setFieldsSaved(true);
                toast(t("survey.features.save.success"), "success");
                setPhase(WIZARD_PHASE.CAMPAIGN);
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail || e.response?.data?.features,
                    t
                );
                toast(detail || t("survey.features.save.error"), "danger");
            });
    };

    const goNext = () => {
        if (phase === WIZARD_PHASE.FIELDS) {
            saveFieldsAndContinue();
            return;
        }
        if (nextPhase) setPhase(nextPhase);
    };

    const renderStepMark = (step, done) => (
        <span className="survey-wizard-step-mark" aria-hidden="true">
            {done ? <TickSquare size={16} aria-hidden="true" /> : step}
        </span>
    );

    const renderStep = (step, done, current, label, enabled) => (
        <li className={stepClassName(done, current)}>
            <button
                type="button"
                className="survey-wizard-step-btn"
                disabled={!enabled}
                aria-current={current ? "step" : undefined}
                onClick={() => goToStep(step)}
            >
                {renderStepMark(step, done)}
                <span>{label}</span>
            </button>
        </li>
    );

    return (
        <>
            <DBModal
                permanent
                show={show}
                minWidth={680}
                onHide={onHide}
                className="survey-form-modal survey-wizard-modal"
            >
                <DBModal.Header
                    closeBtn={onHide}
                    closeLabel={t("survey.settings.help.close")}
                    title={t("survey.wizard.title")}
                />
                <DBModal.Content>
                    <ol
                        className="survey-wizard-steps"
                        aria-label={t("survey.wizard.steps.label")}
                    >
                        {renderStep(
                            1,
                            true,
                            currentStep === 1,
                            t("survey.wizard.step.define"),
                            true
                        )}
                        {renderStep(
                            2,
                            fieldsSaved ||
                                featureRows.length > 0 ||
                                currentStep > 2,
                            currentStep === 2,
                            t("survey.wizard.step.fields"),
                            true
                        )}
                        {renderStep(
                            3,
                            hasSendConfig,
                            currentStep === 3,
                            t("survey.wizard.step.campaign"),
                            true
                        )}
                        {!linkOnly &&
                            renderStep(
                                4,
                                hasSendConfig &&
                                    (phase === WIZARD_PHASE.SEND ||
                                        hasSentInSession),
                                currentStep === 4,
                                t("survey.wizard.step.coins"),
                                hasSendConfig
                            )}
                        {renderStep(
                            linkOnly ? 4 : 5,
                            hasSentInSession,
                            currentStep === (linkOnly ? 4 : 5),
                            t("survey.wizard.step.send"),
                            hasSendConfig
                        )}
                    </ol>
                    {phase === WIZARD_PHASE.DEFINE ? (
                        <>
                            <p className="survey-wizard-lead">
                                {t("survey.wizard.define.lead", {
                                    title: activeSurvey?.title || "",
                                })}
                            </p>
                            {activeSurvey?.description ? (
                                <p className="survey-wizard-help">
                                    {activeSurvey.description}
                                </p>
                            ) : null}
                        </>
                    ) : phase === WIZARD_PHASE.FIELDS ? (
                        <>
                            <p className="survey-wizard-lead">
                                {t("survey.features.lead")}
                            </p>
                            <p className="survey-wizard-help">
                                {activeSurvey?.anonymous_responses
                                    ? t("survey.features.anonymous.help")
                                    : t("survey.features.help")}
                            </p>
                            <FeatureFieldsEditor
                                features={featureRows}
                                onChange={(next) => {
                                    setFeatureRows(next);
                                    setFeatureErrors([]);
                                }}
                                errors={featureErrors}
                            />
                        </>
                    ) : phase === WIZARD_PHASE.INCENTIVE ? (
                        <>
                            <p className="survey-wizard-lead">
                                {t("survey.wizard.coins.lead")}
                            </p>
                            <p className="survey-wizard-help">
                                {t("survey.wizard.coins.help")}
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="survey-wizard-lead">
                                {t("survey.wizard.send.now")}
                            </p>
                            <p className="survey-wizard-help">
                                {linkOnly
                                    ? t("survey.wizard.send.help.link")
                                    : t("survey.wizard.send.help")}
                            </p>
                        </>
                    )}
                    {phase !== WIZARD_PHASE.INCENTIVE &&
                        phase !== WIZARD_PHASE.DEFINE &&
                        phase !== WIZARD_PHASE.FIELDS &&
                        (getCampaignsLoading ? (
                            <DBSpinner tip={t("loading")} />
                        ) : (
                            <>
                                <section
                                    className="survey-wizard-section"
                                    aria-labelledby="survey-wizard-campaigns-heading"
                                >
                                    <div className="survey-wizard-section-head">
                                        <h3 id="survey-wizard-campaigns-heading">
                                            {t(
                                                "survey.wizard.send.section.campaigns"
                                            )}
                                        </h3>
                                        <button
                                            type="button"
                                            className="survey-wizard-section-create"
                                            onClick={openCreateCampaign}
                                        >
                                            <Add
                                                size={16}
                                                aria-hidden="true"
                                            />
                                            {t("survey.campaign.create")}
                                        </button>
                                    </div>
                                    <p className="survey-wizard-section-help">
                                        {t(
                                            "survey.wizard.send.section.campaigns.help"
                                        )}
                                    </p>
                                    {distributionCampaigns.length ? (
                                        <ul
                                            className="survey-wizard-campaigns"
                                            aria-label={t(
                                                "survey.wizard.send.section.campaigns"
                                            )}
                                        >
                                            {distributionCampaigns.map(
                                                (campaign) => {
                                                    const selected =
                                                        selectedCampaignIds.includes(
                                                            campaign.id
                                                        );
                                                    const sent =
                                                        campaignWasSent(
                                                            campaign
                                                        );
                                                    const compatible =
                                                        campaignFitsSurvey(
                                                            campaign,
                                                            activeSurvey
                                                        );
                                                    const phone =
                                                        isPhoneKind(
                                                            campaign.kind
                                                        );
                                                    const kindLabel =
                                                        campaignKindLabel(
                                                            campaign.kind,
                                                            campaign.user_group_type
                                                        );
                                                    const meta = selected
                                                        ? campaignMetaLine(
                                                              campaign
                                                          )
                                                        : "";
                                                    return (
                                                        <li
                                                            key={campaign.id}
                                                            className={[
                                                                selected
                                                                    ? "is-selected"
                                                                    : "",
                                                                sent
                                                                    ? "is-sent"
                                                                    : "",
                                                                compatible
                                                                    ? ""
                                                                    : "is-incompatible",
                                                            ]
                                                                .filter(
                                                                    Boolean
                                                                )
                                                                .join(" ")}
                                                        >
                                                            <label className="survey-wizard-campaign-choice">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        selected &&
                                                                        compatible
                                                                    }
                                                                    disabled={
                                                                        !compatible
                                                                    }
                                                                    title={
                                                                        compatible
                                                                            ? undefined
                                                                            : t(
                                                                                  "survey.wizard.campaign.incompatible"
                                                                              )
                                                                    }
                                                                    onChange={() =>
                                                                        compatible &&
                                                                        toggleCampaignId(
                                                                            campaign.id
                                                                        )
                                                                    }
                                                                />
                                                                <div className="survey-wizard-campaign-main">
                                                                    <strong>
                                                                        {sent ? (
                                                                            <span
                                                                                className="survey-wizard-campaign-sent"
                                                                                title={t(
                                                                                    "survey.wizard.campaign.sent"
                                                                                )}
                                                                                aria-label={t(
                                                                                    "survey.wizard.campaign.sent"
                                                                                )}
                                                                            >
                                                                                <TickCircle
                                                                                    size={
                                                                                        18
                                                                                    }
                                                                                    variant="Bold"
                                                                                    color="#059669"
                                                                                    aria-hidden="true"
                                                                                />
                                                                            </span>
                                                                        ) : null}
                                                                        {
                                                                            campaign.name
                                                                        }
                                                                        <span
                                                                            className={`survey-channel-badge channel-${
                                                                                phone
                                                                                    ? 2
                                                                                    : 1
                                                                            }`}
                                                                        >
                                                                            {campaignDeliveryLabel(
                                                                                campaign.kind
                                                                            )}
                                                                        </span>
                                                                    </strong>
                                                                    <span>
                                                                        {phone
                                                                            ? ""
                                                                            : `${kindLabel} · `}
                                                                        {campaignStatusLabel(
                                                                            campaign.status
                                                                        )}
                                                                        {!compatible
                                                                            ? ` · ${t(
                                                                                  "survey.wizard.campaign.incompatible"
                                                                              )}`
                                                                            : campaignIsUnbound(
                                                                                    campaign
                                                                                )
                                                                              ? ` · ${t(
                                                                                    "survey.wizard.campaign.unbound"
                                                                                )}`
                                                                              : campaign.survey_title &&
                                                                                  !campaignBelongsToSurvey(
                                                                                      campaign,
                                                                                      activeSurvey?.id
                                                                                  )
                                                                                ? ` · ${campaign.survey_title}`
                                                                                : ""}
                                                                    </span>
                                                                    {meta ? (
                                                                        <span className="survey-wizard-campaign-meta">
                                                                            {
                                                                                meta
                                                                            }
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            </label>
                                                            <div className="survey-wizard-campaign-actions">
                                                                <button
                                                                    type="button"
                                                                    aria-label={t(
                                                                        "survey.campaign.action.details"
                                                                    )}
                                                                    title={t(
                                                                        "survey.campaign.action.details"
                                                                    )}
                                                                    onClick={() =>
                                                                        openCampaignDetails(
                                                                            campaign
                                                                        )
                                                                    }
                                                                >
                                                                    <Eye
                                                                        size={
                                                                            16
                                                                        }
                                                                        aria-hidden="true"
                                                                    />
                                                                </button>
                                                                {isLinkCampaign(
                                                                    campaign.kind
                                                                ) &&
                                                                    campaignBelongsToSurvey(
                                                                        campaign,
                                                                        activeSurvey?.id
                                                                    ) && (
                                                                        <button
                                                                            type="button"
                                                                            aria-label={t(
                                                                                "survey.campaign.action.copy.link"
                                                                            )}
                                                                            title={t(
                                                                                "survey.campaign.action.copy.link"
                                                                            )}
                                                                            onClick={() =>
                                                                                handleCopyLink(
                                                                                    campaign
                                                                                )
                                                                            }
                                                                        >
                                                                            <Link1
                                                                                size={
                                                                                    16
                                                                                }
                                                                                aria-hidden="true"
                                                                            />
                                                                        </button>
                                                                    )}
                                                                {isLinkCampaign(
                                                                    campaign.kind
                                                                ) &&
                                                                    campaignBelongsToSurvey(
                                                                        campaign,
                                                                        activeSurvey?.id
                                                                    ) && (
                                                                        <button
                                                                            type="button"
                                                                            aria-label={t(
                                                                                "survey.campaign.action.qr"
                                                                            )}
                                                                            title={t(
                                                                                "survey.campaign.action.qr"
                                                                            )}
                                                                            onClick={() =>
                                                                                handleQr(
                                                                                    campaign
                                                                                )
                                                                            }
                                                                        >
                                                                            <ScanBarcode
                                                                                size={
                                                                                    16
                                                                                }
                                                                                aria-hidden="true"
                                                                            />
                                                                        </button>
                                                                    )}
                                                            </div>
                                                        </li>
                                                    );
                                                }
                                            )}
                                        </ul>
                                    ) : (
                                        <div className="surveys-empty survey-wizard-empty">
                                            {t("survey.wizard.empty")}
                                        </div>
                                    )}
                                </section>
                                {!linkOnly && (
                                    <section
                                        className="survey-wizard-section"
                                        aria-labelledby="survey-wizard-in-system-heading"
                                    >
                                        <div className="survey-wizard-section-head">
                                            <h3 id="survey-wizard-in-system-heading">
                                                {t(
                                                    "survey.wizard.send.section.in_system"
                                                )}
                                            </h3>
                                        </div>
                                        <p className="survey-wizard-section-help">
                                            {t(
                                                "survey.wizard.send.section.in_system.help"
                                            )}
                                        </p>
                                        <ul className="survey-wizard-audiences">
                                            <li>
                                                <label
                                                    className={[
                                                        "survey-wizard-audience",
                                                        allUsersSelected
                                                            ? "is-checked"
                                                            : "",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            allUsersSelected
                                                        }
                                                        onChange={() =>
                                                            toggleAudience(
                                                                USER_AUDIENCE.ALL
                                                            )
                                                        }
                                                    />
                                                    <span>
                                                        {t(
                                                            "survey.wizard.send.audience.all"
                                                        )}
                                                    </span>
                                                </label>
                                            </li>
                                            <li>
                                                <label
                                                    className={[
                                                        "survey-wizard-audience",
                                                        clubAllSelected &&
                                                        !allUsersSelected
                                                            ? "is-checked"
                                                            : "",
                                                        allUsersSelected
                                                            ? "is-disabled"
                                                            : "",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                >
                                                    <input
                                                        ref={clubParentRef}
                                                        type="checkbox"
                                                        checked={
                                                            clubAllSelected
                                                        }
                                                        disabled={
                                                            allUsersSelected
                                                        }
                                                        onChange={
                                                            toggleClubParent
                                                        }
                                                    />
                                                    <span>
                                                        {t(
                                                            "survey.wizard.send.audience.club"
                                                        )}
                                                    </span>
                                                </label>
                                                <ul className="survey-wizard-audience-children">
                                                    {CLUB_USER_AUDIENCES.map(
                                                        (audience) => {
                                                            const checked =
                                                                inSystemAudiences.includes(
                                                                    audience
                                                                );
                                                            return (
                                                                <li
                                                                    key={
                                                                        audience
                                                                    }
                                                                >
                                                                    <label
                                                                        className={[
                                                                            "survey-wizard-audience",
                                                                            checked &&
                                                                            !allUsersSelected
                                                                                ? "is-checked"
                                                                                : "",
                                                                            allUsersSelected
                                                                                ? "is-disabled"
                                                                                : "",
                                                                        ]
                                                                            .filter(
                                                                                Boolean
                                                                            )
                                                                            .join(
                                                                                " "
                                                                            )}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={
                                                                                checked
                                                                            }
                                                                            disabled={
                                                                                allUsersSelected
                                                                            }
                                                                            onChange={() =>
                                                                                toggleAudience(
                                                                                    audience
                                                                                )
                                                                            }
                                                                        />
                                                                        <span>
                                                                            {t(
                                                                                IN_SYSTEM_AUDIENCE_LABEL[
                                                                                    audience
                                                                                ]
                                                                            )}
                                                                        </span>
                                                                    </label>
                                                                </li>
                                                            );
                                                        }
                                                    )}
                                                </ul>
                                            </li>
                                        </ul>
                                    </section>
                                )}
                            </>
                        ))}
                    <span className="sr-only" aria-live="polite">
                        {t("survey.wizard.step.current", {
                            step: currentStep,
                        })}
                    </span>
                </DBModal.Content>
                <DBModal.Footer>
                    {phase !== WIZARD_PHASE.DEFINE && (
                        <DBButton
                            caption={t("survey.wizard.back")}
                            type="secondary"
                            onClick={goBack}
                        />
                    )}
                    {phase !== WIZARD_PHASE.SEND && (
                        <DBButton
                            caption={nextStepCaption}
                            onClick={goNext}
                            loading={
                                phase === WIZARD_PHASE.FIELDS &&
                                saveFeaturesLoading
                            }
                        />
                    )}
                    {phase === WIZARD_PHASE.DEFINE && (
                        <>
                            <DBButton
                                caption={t("survey.wizard.define.edit")}
                                type="secondary"
                                onClick={() => setDefineModal(true)}
                            />
                            <DBButton
                                caption={t("survey.wizard.later")}
                                type="secondary"
                                onClick={onHide}
                            />
                        </>
                    )}
                    {phase === WIZARD_PHASE.FIELDS && (
                        <DBButton
                            caption={t("survey.wizard.fields.skip")}
                            type="secondary"
                            onClick={() => setPhase(WIZARD_PHASE.CAMPAIGN)}
                        />
                    )}
                    {phase === WIZARD_PHASE.CAMPAIGN && (
                        <DBButton
                            caption={t("survey.wizard.later")}
                            type="secondary"
                            onClick={onHide}
                        />
                    )}
                    {phase === WIZARD_PHASE.INCENTIVE && (
                        <DBButton
                            caption={t("survey.wizard.coins.action")}
                            type="secondary"
                            onClick={() => setIncentiveWizard(true)}
                        />
                    )}
                    {phase === WIZARD_PHASE.SEND && (
                        <>
                            <DBButton
                                caption={
                                    <>
                                        <Send2
                                            size={18}
                                            aria-hidden="true"
                                        />{" "}
                                        {selectedSendableCampaigns.length >
                                            0 &&
                                        selectedSendableCampaigns.every(
                                            (campaign) =>
                                                campaign.status ===
                                                CAMPAIGN_STATUS.SENT
                                        ) &&
                                        !hasInSystemSelection
                                            ? t(
                                                  "survey.campaign.action.resend"
                                              )
                                            : t(
                                                  "survey.campaign.action.send"
                                              )}
                                    </>
                                }
                                disabled={!canSend}
                                onClick={requestSend}
                            />
                            <DBButton
                                caption={
                                    hasSendConfig
                                        ? t("survey.wizard.later.send")
                                        : t("survey.wizard.later")
                                }
                                type="secondary"
                                onClick={onHide}
                            />
                            {hasSentInSession && (
                                <DBButton
                                    caption={t("finish.label")}
                                    onClick={onHide}
                                />
                            )}
                        </>
                    )}
                </DBModal.Footer>
            </DBModal>

            {activeSurvey?.id && (
                <CampaignModal
                    stacked
                    show={campaignModal.show}
                    survey={activeSurvey}
                    campaign={campaignModal.campaign}
                    linkOnly={linkOnly}
                    onHide={finishCampaignModal}
                    onSuccess={(nextCampaign, options = {}) => {
                        const createdNow =
                            campaignModal.isCreate ||
                            !campaignModal.campaign?.id;
                        loadCampaigns(nextCampaign?.id);
                        if (createdNow && nextCampaign?.id) {
                            pendingAfterCreateRef.current = true;
                        }
                        if (options.keepOpen && nextCampaign?.id) {
                            setCampaignModal({
                                show: true,
                                campaign: nextCampaign,
                                isCreate: false,
                            });
                            return;
                        }
                        finishCampaignModal();
                    }}
                />
            )}
            {activeSurvey?.id && (
                <SurveyFormModal
                    stacked
                    show={defineModal}
                    survey={activeSurvey}
                    personal={linkOnly}
                    onHide={() => setDefineModal(false)}
                    onSuccess={(saved) => {
                        setDefineModal(false);
                        if (saved?.id) {
                            setSurveyState(saved);
                            onSurveyChange?.(saved);
                        }
                    }}
                />
            )}
            <CampaignWizard
                stacked
                show={incentiveWizard}
                preset={incentivePreset}
                onHide={() => setIncentiveWizard(false)}
                onPublished={() => {
                    toast(t("survey.wizard.coins.done"), "success");
                    setIncentiveWizard(false);
                    setPhase(WIZARD_PHASE.SEND);
                }}
            />
            <DBConfirmModal
                stacked
                show={!!confirmSend}
                onHide={() => setConfirmSend(null)}
                title={
                    confirmSend?.campaigns?.length > 0 &&
                    confirmSend.campaigns.every(
                        (campaign) =>
                            campaign.status === CAMPAIGN_STATUS.SENT
                    ) &&
                    !(confirmSend.audiences || []).length
                        ? t("survey.campaign.resend.confirm.title")
                        : t("survey.campaign.send.confirm.title")
                }
                description={
                    confirmSend?.campaigns?.length > 0 &&
                    confirmSend.campaigns.every(
                        (campaign) =>
                            campaign.status === CAMPAIGN_STATUS.SENT
                    ) &&
                    !(confirmSend.audiences || []).length
                        ? t("survey.campaign.resend.confirm.description")
                        : t("survey.wizard.send.confirm.description")
                }
                btnTitle={
                    confirmSend?.campaigns?.length > 0 &&
                    confirmSend.campaigns.every(
                        (campaign) =>
                            campaign.status === CAMPAIGN_STATUS.SENT
                    ) &&
                    !(confirmSend.audiences || []).length
                        ? t("survey.campaign.action.resend")
                        : t("survey.campaign.action.send")
                }
                loading={actionLoading}
                onConfirm={() => handleConfirmSend()}
            />
            {gate}
        </>
    );
}
